// src/components/auth/rpBootAdapter.ts
//
// H-2C2 WP-6 — adapts the OIDC RP `/auth/context` payload into the exact
// `bootContext` hydration shape the CRM app shell already expects.
//
// The RP endpoint returns `SuiteRuntimeContext` — Central's switch-context
// payload normalized by the CRM backend, with `meta.cacheTtlSeconds` flattened
// to a top-level `cacheTtlSeconds`. That is the SAME underlying Central data
// the legacy Bearer-model boot path receives. So this adapter reuses the
// legacy path's own normalizer (`mapSwitchContextResponse`) verbatim: both
// boot paths produce an identical `SwitchContextResponse` for `setBootReady`,
// which eliminates any divergence between the legacy `BootGate` and
// `RpBootGate` hydration by construction.
//
// No local derivation: `mapSwitchContextResponse` only renames/normalizes
// Central's field names — it computes no apps, entitlements, roles, workspace
// validity, membership, or default app.

import { mapSwitchContextResponse, type SwitchContextResponse } from '../../api/centralSuiteApi';
import type { RpRuntimeContext } from '../../api/rpContextApi';

/**
 * Convert an RP runtime context into the `setBootReady` payload. Returns
 * `null` when the context cannot yield a usable workspace/user — the caller
 * treats that as a `malformed` recovery (fail closed; never boot half-ready).
 */
export function rpContextToBootReady(
  context: RpRuntimeContext,
): SwitchContextResponse | null {
  // Pass Central's authoritative browser-safe `meta` straight through so the
  // shared normalizer hydrates `bootContext.meta` identically to the
  // Bearer/direct path — in particular `features.uiShell`, which the shell
  // predicate reads. Backward-compatible: an older BFF response without `meta`
  // falls back to the cacheTtlSeconds-only shape (legacy shell when uiShell is
  // genuinely absent). No reconstruction from cacheTtlSeconds, no hardcoding.
  const meta = context.meta ?? { cacheTtlSeconds: context.cacheTtlSeconds };
  const mapped = mapSwitchContextResponse({
    ...context,
    meta,
  });
  if (!mapped.currentWorkspace || !mapped.user) {
    return null;
  }
  return mapped;
}
